import get from 'lodash.get';
import { combineReducers } from 'redux';
import { takeEvery, call, select, put, take, cancel, fork } from 'redux-saga/effects';
import queryString from 'query-string';
import { SubmissionError } from 'redux-form';
import { LOCATION_CHANGE } from 'react-router-redux';
import shuffle from 'lodash.shuffle';
import { showAlert } from './alert';
import request from '../api-utils/request';
import actionKey from '../utils/action-key';
import { LOGOUT } from './auth';
import { STUDENT_VIEW } from './app';
import { SET_ME } from './me';
import SPARTAN_USER_TYPES from '../../constants/spartan-user-types';


export const FETCH_COLLEGES = 'colleges/FETCH_COLLEGES';
export const TOGGLE_CONNECT_MODAL = 'colleges/TOGGLE_CONNECT_MODAL';
export const UPDATE_CONNECTIONS = 'colleges/UPDATE_CONNECTIONS';
export const DISABLE_CONNECT = 'colleges/DISABLE_CONNECT';
export const DISABLE_CONNECT_MODE = 'colleges/DISABLE_CONNECT_MODE';
export const SUBMIT_TO_VELOCIFY = 'colleges/SUBMIT_TO_VELOCIFY';
export const TOGGLE_SPARTAN_CONNECT_MODAL = 'colleges/TOGGLE_SPARTAN_CONNECT_MODAL';
export const CHANGE_FILTER = 'colleges/CHANGE_FILTER';

const list = (state = [], action) => {
    switch (action.type) {
        case CHANGE_FILTER: {
            return [];
        }
        case actionKey.init(FETCH_COLLEGES):
            if (!action.offset || action.offset === 0) {
                return [];
            }
            return state;
        case actionKey.success(FETCH_COLLEGES):
            if (!action.offset || action.offset === 0) {
                return action.payload.colleges;
            }
            return [
                ...state,
                ...action.payload.colleges,
            ];
        default:
            return state;
    }
};

const connectionsReducer = (state = {}, action) => {
    switch (action.type) {
        case actionKey.success(FETCH_COLLEGES):
            return action.payload.connections;
        case actionKey.success(UPDATE_CONNECTIONS):
            return {
                ...state,
                [action.collegeId]: {
                    ...state[action.collegeId],
                    programs: action.payload.connections,
                },
            };
        default:
            return state;
    }
};

const loadingReducer = (state = false, action) => {
    switch (action.type) {
        case actionKey.init(FETCH_COLLEGES):
            return true;
        case actionKey.failure(FETCH_COLLEGES):
        case actionKey.success(FETCH_COLLEGES):
            return false;
        default:
            return state;
    }
};

const errorReducer = (state = null, action) => {
    switch (action.type) {
        case FETCH_COLLEGES:
        case actionKey.init(FETCH_COLLEGES):
        case actionKey.success(FETCH_COLLEGES):
            return null;
        case actionKey.failure(FETCH_COLLEGES):
            return action.error;
        default:
            return state;
    }
};

const connectModal = (state = null, action) => {
    switch (action.type) {
        case TOGGLE_CONNECT_MODAL:
            return action.payload;
        case actionKey.success(UPDATE_CONNECTIONS):
            return null;
        default:
            return state;
    }
};

const spartanModal = (state = null, action) => {
    switch (action.type) {
        case TOGGLE_SPARTAN_CONNECT_MODAL:
            return action.payload;
        case LOCATION_CHANGE:
            return null;
        case DISABLE_CONNECT_MODE:
            return null;
        default:
            return state;
    }
};

export const maxReached = (state = false, action) => {
    switch (action.type) {
        case actionKey.success(FETCH_COLLEGES):
            return action.payload.colleges.length < 10;
        default:
            return state;
    }
};

export const sponsoredReducer = (state = null, action) => {
    switch (action.type) {
        case actionKey.success(FETCH_COLLEGES): {
            if (state) {
                return state;
            }
            const sposored = shuffle(action.payload.colleges)
                .find(({ sponsored }) => sponsored);
            return sposored || null;
        }
        default:
            return state;
    }
};

export const disableConnect = (state = false, action) => {
    switch (action.type) {
        case DISABLE_CONNECT:
        case actionKey.init(SUBMIT_TO_VELOCIFY):
            return true;
        default:
            return state;
    }
};

export const filterReducer = (state = {
    state: null,
}, action) => {
    switch (action.type) {
        case LOCATION_CHANGE:
            return {
                state: null,
            };
        case DISABLE_CONNECT_MODE:
            return {
                state: null,
            };
        case CHANGE_FILTER:
            return {
                ...state,
                ...action.payload,
            };
        default:
            return state;
    }
};

export default combineReducers({
    list,
    loading: loadingReducer,
    error: errorReducer,
    connections: connectionsReducer,
    modal: connectModal,
    maxReached,
    sponsored: sponsoredReducer,
    disableConnect,
    spartanModal,
    filter: filterReducer,
});

export const fetchCollegesAction = (payload, { reject, resolve } = {}) => ({
    type: FETCH_COLLEGES,
    reject,
    resolve,
    payload,
});

export const toggleConnectModalAction = payload => ({
    type: TOGGLE_CONNECT_MODAL,
    payload,
});

export const updateConnectionsAction = payload => ({
    type: UPDATE_CONNECTIONS,
    payload,
});

export const disableConnectAction = () => ({
    type: DISABLE_CONNECT,
});

export const disableConnectModeAction = () => ({
    type: DISABLE_CONNECT_MODE,
});

export const submitToVelocifyAction = ({ resolve, reject, levelOfInterests } = {}) => ({
    type: SUBMIT_TO_VELOCIFY,
    resolve,
    reject,
    levelOfInterests,
});

export const toggleSpartanConnectModalAction = payload => ({
    type: TOGGLE_SPARTAN_CONNECT_MODAL,
    payload,
});

export const changeFilterAction = payload => ({
    type: CHANGE_FILTER,
    payload,
});

export function* rootSaga() {
    yield [
        takeEvery(DISABLE_CONNECT_MODE, disableConnectMode),
        takeEvery(FETCH_COLLEGES, fetchColleges),
        takeEvery(UPDATE_CONNECTIONS, updateConnections),
        takeEvery(LOCATION_CHANGE, disableConnectOnRoute),
        takeEvery(actionKey.success(FETCH_COLLEGES), disableConnectSaga),
        takeEvery(SET_ME, disableConnectWhenOnCampusSaga),
        takeEvery(SUBMIT_TO_VELOCIFY, submitToVelocify),
        takeEvery(CHANGE_FILTER, fetchColleges),
    ];
}


const noop = () => null;

function* fetchColleges({ type, reject = noop, resolve = noop, payload = {} }) {
    const state = yield select();
    let query = {};
    if (type === FETCH_COLLEGES) {
        query = {
            ...payload,
            ...state.colleges.filter,
        };
    } else {
        query = {
            ...state.colleges.filter,
        };
    }
    const res = yield call(request, {
        url: `/api/colleges?${queryString.stringify(query)}`,
        type: FETCH_COLLEGES,
        ...payload,
    });

    if (res && res.error) {
        reject(res.error);
    } else if (res) {
        resolve(res.payload);
    }
}

function* updateConnections({
    type,
    payload: {
        reject = noop,
        resolve = noop,
        collegeId,
        connections,
        connectedAlert = true,
    },
}) {
    const { colleges, router: { location }, theme } = yield select();
    const res = yield call(request, {
        url: `/api/college/${collegeId}/connections`,
        type,
        options: {
            body: {
                connections,
            },
            method: 'PUT',
        },
        collegeId,
    });
    if (res.error) {
        reject(new SubmissionError({
            _error: res.error,
        }));
        return;
    }
    resolve(res.payload);
    if (!connectedAlert) {
        return;
    }
    const college = colleges.list.find(c => c.collegeID_PK === +collegeId);
    const name = college ? college.name : null;
    const existingConnections = get(colleges.connections, collegeId, []);
    const alreadyConnected = existingConnections && existingConnections.length > 0;
    const alert = {
        title: alreadyConnected ? 'Changes Saved!' : 'Connection Complete!',
        message: alreadyConnected ? 'Your program changes have been saved.' :
            `You are now connected${name ? ` with ${name}` : ''}.`,
        type: 'success',
        button: 'Done',
    };
    if (location.pathname === '/colleges' && 's' in queryString.parse(location.search)) {
        alert.link = '/colleges';
    }
    if (!theme.collegeMultiApply) {
        alert.link = '/results';
    }
    yield put(showAlert(alert));
}


export function* disableConnectSaga({ type, payload }) {
    const { theme, app, colleges } = yield select();
    if (!theme.connectMode ||
        theme.collegeMultiApply ||
        app.view !== STUDENT_VIEW ||
        colleges.disableConnect) {
        return;
    }
    if (type === actionKey.success(FETCH_COLLEGES) && Object.keys(payload.connections).length > 0) {
        yield put(disableConnectAction());
    }
}

// probably not needed but just in case
export function* disableConnectWhenOnCampusSaga() {
    const { theme, me } = yield select();
    if (theme.companyDomain !== 'spartan') {
        return;
    }
    if (me.spartanUserType !== SPARTAN_USER_TYPES.ON_CAMPUS_VISIT) {
        return;
    }
    yield put(disableConnectAction());
}

// when moving from /colleges => /results page disable connect button
export function* disableConnectOnRoute() {
    const { theme, app, router, colleges } = yield select();
    if (!theme.connectMode ||
        theme.collegeMultiApply ||
        theme.companyDomain !== 'nustaredu' ||
        app.view !== STUDENT_VIEW ||
        colleges.disableConnect
    ) {
        return;
    }
    const pathname = get(router, 'location.pathname', '/');

    if (pathname !== '/colleges') {
        return;
    }

    const task = yield fork(submitToVelocifyOnResultPage);

    yield take(LOGOUT);

    yield cancel(task);
}

export function* disableConnectMode() {
    yield take(LOGOUT);
    const task = yield fork(submitToVelocifyOnResultPage);

    yield take(LOGOUT);

    yield cancel(task);
}

export function* submitToVelocifyOnResultPage() {
    while (true) { // eslint-disable-line no-constant-condition
        const action = yield take(LOCATION_CHANGE);
        if (action.payload.pathname === '/results') {
            yield put(submitToVelocifyAction());
            break;
        }
    }
}

export function* submitToVelocify({ type, levelOfInterests, reject = noop, resolve = noop }) {
    const { theme, colleges } = yield select();
    if (theme.companyDomain !== 'spartan' ||
        colleges.disableConnect) {
        resolve();
        return;
    }

    const result = yield call(request, {
        url: '/api/spartan-velocify',
        type,
        options: {
            method: 'POST',
            body: {
                levelOfInterests,
            },
        },
    });

    if (result.error) {
        reject(new SubmissionError({
            _error: result.error,
        }));
    } else {
        resolve();
    }
}



// WEBPACK FOOTER //
// ./src/client/redux/colleges.js